// src/heuristic_roster_enhanced.ts
// Enhanced version that matches MILP constraints
// Also re-exports all utilities from original for compatibility

import { localCellToBitIdx, TOTAL_SLOTS, utcRangeToAvailabilityBitRanges } from "./lib/time";

export interface HeuristicCharacter {
    id: string;
    name: string;
    ownerId: string | null;
    guildId: string | null;
    roles: string[];
    className: string;
    ilvl?: number;
    availabilityBits?: string | null;
}

export interface EnhancedCharacter extends HeuristicCharacter {
    // CRITICAL: Match MILP constraints
    region: string;
    realm: string;
    faction: string;
    raidInterests: string[];      // Instance IDs they want to raid
    instanceLockouts: string[];   // Instance IDs they've already raided this week
}

export interface AssignmentResult {
    slotIndex: number;
    characterId: string;
    roleLabel: string;
    isExternal: boolean;
}

export interface EnhancedAssignmentResult extends AssignmentResult {}

const SLOTS_PER_DAY = 48;
const TOTAL_AVAIL_SLOTS = TOTAL_SLOTS;
const DAYS_APP = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Half-hour ranges into the 336-bit availability string for `weekStartIso`, using the same
 * lockout-week + userTz geometry as `localCellToBitIdx` (see `utcRangeToAvailabilityBitRanges`).
 */
export function getSessionWindows(
    sessions: { startsAt: string | Date; durationMinutes: number }[],
    weekStartIso: string,
    userTz: string
): [number, number][] {
    const windows: [number, number][] = [];

    for (const s of sessions) {
        const d =
            typeof s.startsAt === "string"
                ? new Date(s.startsAt)
                : s.startsAt instanceof Date
                  ? s.startsAt
                  : new Date(String(s.startsAt));
        if (Number.isNaN(d.getTime())) continue;

        const durationMinutes = Math.max(1, Number(s.durationMinutes) || 0);
        const end = new Date(d.getTime() + durationMinutes * 60000);
        const ranges = utcRangeToAvailabilityBitRanges(weekStartIso, d, end, userTz);
        for (const r of ranges) {
            windows.push(r);
        }
    }

    return windows;
}

/**
 * Recurring day/time patterns interpreted in `userTz`, mapped into the 336-bit UTC string for `weekStartIso`
 * (Wednesday lockout week start, YYYY-MM-DD).
 */
export function getRecurringRaidWindows(
    patterns: Record<string, { start: string; end: string }>,
    weekStartIso: string,
    userTz: string
): [number, number][] {
    const windows: [number, number][] = [];

    for (let dayLocalMon0 = 0; dayLocalMon0 < 7; dayLocalMon0++) {
        const dayName = DAYS_APP[dayLocalMon0];
        const pattern = patterns[dayName];
        if (!pattern) continue;

        const [sh, sm] = pattern.start.split(":").map((x) => Number(x));
        const [eh, em] = pattern.end.split(":").map((x) => Number(x));
        if (Number.isNaN(sh) || Number.isNaN(sm) || Number.isNaN(eh) || Number.isNaN(em)) continue;

        let startMins = sh * 60 + sm;
        let endMins = eh * 60 + em;
        if (endMins <= startMins) endMins += 24 * 60;

        const slots = Math.ceil((endMins - startMins) / 30);
        const startHalf = Math.floor(startMins / 30);

        const indices: number[] = [];
        let d = dayLocalMon0;
        let slot = startHalf;
        for (let i = 0; i < slots; i++) {
            indices.push(localCellToBitIdx(weekStartIso, d, slot, userTz));
            slot += 1;
            if (slot >= SLOTS_PER_DAY) {
                slot = 0;
                d = (d + 1) % 7;
            }
        }

        const uniq = Array.from(new Set(indices)).sort((a, b) => a - b);
        let j = 0;
        while (j < uniq.length) {
            const rs = uniq[j];
            let k = j;
            while (k + 1 < uniq.length && uniq[k + 1] === uniq[k] + 1) k++;
            const re = Math.min(uniq[k] + 1, TOTAL_AVAIL_SLOTS);
            windows.push([rs, re]);
            j = k + 1;
        }
    }

    return windows;
}

/**
 * RE-EXPORT: CHECKS AVAILABILITY - Original version (basic check)
 */
export function isCharacterAvailable(
    char: HeuristicCharacter,
    windows: [number, number][] | null
): boolean {
    if (!windows || windows.length === 0) return true; 
    if (!char.availabilityBits) return false; 
  
    for (const [start, end] of windows) {
        if (end > char.availabilityBits.length) return false;
        
        for (let i = start; i < end; i++) {
            if (char.availabilityBits[i] !== "1") {
                return false;
            }
        }
    }
    return true;
}

/**
 * ENHANCED: Checks availability for FULL RAID DURATION (not just start time)
 */
function isAvailableForDuration(
    char: EnhancedCharacter | HeuristicCharacter,
    windows: [number, number][] | null
): boolean {
    // Same implementation as isCharacterAvailable
    return isCharacterAvailable(char, windows);
}

function getCompositionTargets(size: number): {
    Tank: number;
    Healer: number;
    "Melee DPS": number;
    "Ranged DPS": number;
} {
    let tanks = 2;
    let healers = 2;
    
    if (size >= 25) { tanks = 3; healers = 5; }
    if (size >= 40) { tanks = 3; healers = 8; }
    
    const remainingForDPS = Math.max(0, size - tanks - healers);
    const melee = Math.floor(remainingForDPS / 2);
    const ranged = remainingForDPS - melee;
    
    return {
        Tank: tanks,
        Healer: healers,
        "Melee DPS": melee,
        "Ranged DPS": ranged,
    };
}

/**
 * RE-EXPORT: Original auto-fill (for backward compatibility)
 */
export function autoFillRoster(
    size: number,
    currentSlots: (string | null)[],
    currentRoles: (string | null)[],
    candidates: HeuristicCharacter[],
    hostingGuildId: string,
    raidWindows?: [number, number][] | null,
    ignoreAvailability: boolean = false
): AssignmentResult[] {
    const assignments: AssignmentResult[] = [];
    const targets = getCompositionTargets(size);
  
    const currentCounts: Record<string, number> = {
        Tank: 0, Healer: 0, "Melee DPS": 0, "Ranged DPS": 0,
    };
  
    const usedOwnerIds = new Set<string>();
    const seatedCharIds = new Set<string>();
  
    currentSlots.forEach((charId, idx) => {
        if (!charId) return;
        seatedCharIds.add(charId);
        
        const charDef = candidates.find(c => c.id === charId);
        if (charDef && charDef.ownerId) {
            usedOwnerIds.add(charDef.ownerId);
        }
  
        const role = currentRoles[idx];
        if (role && role in currentCounts) {
            currentCounts[role]++;
        }
    });
  
    const pool = candidates.filter(c => {
        if (seatedCharIds.has(c.id)) return false; 
        if (c.ownerId && usedOwnerIds.has(c.ownerId)) return false;
        
        // Character-level lockout check (already did this raid instance this week)
        if ((c as any).isLockedOut === true) return false;
        
        // Availability check for raid time windows
        if (!ignoreAvailability && raidWindows) {
            if (!isCharacterAvailable(c, raidWindows)) {
                return false;
            }
        }
        return true;
    });
  
    pool.sort((a, b) => {
        const aInGuild = a.guildId === hostingGuildId;
        const bInGuild = b.guildId === hostingGuildId;
        if (aInGuild && !bInGuild) return -1;
        if (!aInGuild && bInGuild) return 1;
  
        const aLvl = a.ilvl || 0;
        const bLvl = b.ilvl || 0;
        if (aLvl !== bLvl) return bLvl - aLvl;
  
        return 0;
    });
  
    const needs: string[] = [];
    function addNeeds(role: string, target: number) {
        const current = currentCounts[role] || 0;
        const missing = Math.max(0, target - current);
        for (let i = 0; i < missing; i++) needs.push(role);
    }
  
    addNeeds("Tank", targets.Tank);
    addNeeds("Healer", targets.Healer);
    addNeeds("Melee DPS", targets["Melee DPS"]);
    addNeeds("Ranged DPS", targets["Ranged DPS"]);
  
    const emptyIndices = currentSlots
        .map((val, idx) => (val === null ? idx : -1))
        .filter(idx => idx !== -1);
  
    let slotCursor = 0;
  
    for (const neededRole of needs) {
        if (slotCursor >= emptyIndices.length) break;
  
        const candidateIndex = pool.findIndex(c => {
            if (c.ownerId && usedOwnerIds.has(c.ownerId)) return false;
            return c.roles && c.roles.includes(neededRole);
        });
  
        if (candidateIndex !== -1) {
            const candidate = pool[candidateIndex];
            assignments.push({
                slotIndex: emptyIndices[slotCursor],
                characterId: candidate.id,
                roleLabel: neededRole,
                isExternal: candidate.guildId !== hostingGuildId
            });
            if (candidate.ownerId) usedOwnerIds.add(candidate.ownerId);
            pool.splice(candidateIndex, 1);
            slotCursor++;
        }
    }
  
    while (slotCursor < emptyIndices.length) {
        const candidateIndex = pool.findIndex(c => {
            if (c.ownerId && usedOwnerIds.has(c.ownerId)) return false;
            return c.roles && (c.roles.includes("Melee DPS") || c.roles.includes("Ranged DPS"));
        });
  
        if (candidateIndex !== -1) {
            const candidate = pool[candidateIndex];
            const role = candidate.roles.find((r: string) => r === "Melee DPS" || r === "Ranged DPS") || "Melee DPS";
            
            assignments.push({
                slotIndex: emptyIndices[slotCursor],
                characterId: candidate.id,
                roleLabel: role,
                isExternal: candidate.guildId !== hostingGuildId
            });
            if (candidate.ownerId) usedOwnerIds.add(candidate.ownerId);
            pool.splice(candidateIndex, 1);
            slotCursor++;
        } else {
            break;
        }
    }
  
    return assignments;
}

/**
 * ENHANCED: Auto-fill with MILP-style constraints
 * 
 * CRITICAL CONSTRAINTS (matching MILP):
 * 1. Region/Realm/Faction must match raid
 * 2. Character must have instance in raid_interests
 * 3. Character must NOT have instance lockout
 * 4. Character must be available for FULL duration
 * 5. Human bilocation (owner can't be in 2 places)
 * 
 * PRIORITIES:
 * 1. Guild members first
 * 2. Higher ilvl
 * 3. Role availability
 */
export function autoFillRosterEnhanced(
    size: number,
    currentSlots: (string | null)[],
    currentRoles: (string | null)[],
    candidates: EnhancedCharacter[],
    raidConfig: {
        instanceId: string;      // Which instance is this raid?
        hostingGuildId: string;
        region: string;
        realm: string;
        faction: string;
        raidWindows: [number, number][] | null;
    },
    ignoreAvailability: boolean = false
): EnhancedAssignmentResult[] {
    const assignments: EnhancedAssignmentResult[] = [];
    
    // Composition targets
    const targets = getCompositionTargets(size);
    
    const currentCounts: Record<string, number> = {
        Tank: 0, Healer: 0, "Melee DPS": 0, "Ranged DPS": 0,
    };
    
    const usedOwnerIds = new Set<string>();
    const seatedCharIds = new Set<string>();
    
    // 1. Analyze existing roster
    currentSlots.forEach((charId, idx) => {
        if (!charId) return;
        seatedCharIds.add(charId);
        
        const charDef = candidates.find(c => c.id === charId);
        if (charDef && charDef.ownerId) {
            usedOwnerIds.add(charDef.ownerId);
        }
        
        const role = currentRoles[idx];
        if (role && role in currentCounts) {
            currentCounts[role]++;
        }
    });
    
    // 2. ENHANCED FILTERING: Match MILP constraints
    const pool = candidates.filter(c => {
        // Already seated
        if (seatedCharIds.has(c.id)) return false;
        
        // Human bilocation
        if (c.ownerId && usedOwnerIds.has(c.ownerId)) return false;
        
        // CONSTRAINT: Region/Realm/Faction must match
        if (c.region !== raidConfig.region) return false;
        if (c.realm !== raidConfig.realm) return false;
        if (c.faction !== raidConfig.faction) return false;
        
        // CONSTRAINT: Must have raid interest for this instance
        if (!c.raidInterests.includes(raidConfig.instanceId)) return false;
        
        // CONSTRAINT: Must NOT have instance lockout
        if (c.instanceLockouts.includes(raidConfig.instanceId)) return false;
        
        // CONSTRAINT: Must be available for FULL duration
        if (!ignoreAvailability && raidConfig.raidWindows) {
            if (!isAvailableForDuration(c, raidConfig.raidWindows)) {
                return false;
            }
        }
        
        return true;
    });
    
    // 3. Sort: Guild Members > iLvl
    pool.sort((a, b) => {
        const aInGuild = a.guildId === raidConfig.hostingGuildId;
        const bInGuild = b.guildId === raidConfig.hostingGuildId;
        if (aInGuild && !bInGuild) return -1;
        if (!aInGuild && bInGuild) return 1;
        
        const aLvl = a.ilvl || 0;
        const bLvl = b.ilvl || 0;
        if (aLvl !== bLvl) return bLvl - aLvl;
        
        return 0;
    });
    
    // 4. Calculate needs
    const needs: string[] = [];
    function addNeeds(role: string, target: number) {
        const current = currentCounts[role] || 0;
        const missing = Math.max(0, target - current);
        for (let i = 0; i < missing; i++) needs.push(role);
    }
    
    addNeeds("Tank", targets.Tank);
    addNeeds("Healer", targets.Healer);
    addNeeds("Melee DPS", targets["Melee DPS"]);
    addNeeds("Ranged DPS", targets["Ranged DPS"]);
    
    // 5. Fill slots
    const emptyIndices = currentSlots
        .map((val, idx) => (val === null ? idx : -1))
        .filter(idx => idx !== -1);
    
    let slotCursor = 0;
    
    // Pass 1: Strict role match
    for (const neededRole of needs) {
        if (slotCursor >= emptyIndices.length) break;
        
        const candidateIndex = pool.findIndex(c => {
            if (c.ownerId && usedOwnerIds.has(c.ownerId)) return false;
            return c.roles && c.roles.includes(neededRole);
        });
        
        if (candidateIndex !== -1) {
            const candidate = pool[candidateIndex];
            assignments.push({
                slotIndex: emptyIndices[slotCursor],
                characterId: candidate.id,
                roleLabel: neededRole,
                isExternal: candidate.guildId !== raidConfig.hostingGuildId
            });
            if (candidate.ownerId) usedOwnerIds.add(candidate.ownerId);
            pool.splice(candidateIndex, 1);
            slotCursor++;
        }
    }
    
    // Pass 2: Loose DPS fill
    while (slotCursor < emptyIndices.length) {
        const candidateIndex = pool.findIndex(c => {
            if (c.ownerId && usedOwnerIds.has(c.ownerId)) return false;
            return c.roles && (c.roles.includes("Melee DPS") || c.roles.includes("Ranged DPS"));
        });
        
        if (candidateIndex !== -1) {
            const candidate = pool[candidateIndex];
            const role = candidate.roles.find(r => r === "Melee DPS" || r === "Ranged DPS") || "Melee DPS";
            
            assignments.push({
                slotIndex: emptyIndices[slotCursor],
                characterId: candidate.id,
                roleLabel: role,
                isExternal: candidate.guildId !== raidConfig.hostingGuildId
            });
            if (candidate.ownerId) usedOwnerIds.add(candidate.ownerId);
            pool.splice(candidateIndex, 1);
            slotCursor++;
        } else {
            break;
        }
    }
    
    return assignments;
}
